import { Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';

import { PlaylistApi } from '../../api/playlist.api';
import { timeDelay } from '../../helpers/time.helpers';
import { BrowseFilter } from '../../models/filter';
import { BrowseSorting } from '../../models/sorting';
import { Sport } from '../../models/sport';
import { Store } from '../../../../shared/store';
import { PlaylistState } from './playlist.state';
import { Playlist } from '@kinomap-dev/ng-ui-lib/lib/models/playlist.model';

const playListState = new PlaylistState();

@Injectable({ providedIn: 'root' })
export class PlaylistStore extends Store<PlaylistState> {
  playlists$ = this.state$.pipe(map((s) => s.playlists));
  isLoadingPlaylists$ = this.state$.pipe(map((s) => s.isLoadingPlaylists));
  arePlaylistsComplete$ = this.state$.pipe(map((s) => s.arePlaylistsComplete));
  homePlaylists$ = this.state$.pipe(map((s) => s.homePlaylists));

  constructor(private playlistApi: PlaylistApi) {
    super(playListState);
  }

  // SET PLAYLISTS

  setPlaylists(nextPlaylists: Playlist[]): void {
    this.setState({ ...this.state, playlists: nextPlaylists });
  }
  setIsLoadingPlaylists(nextIsLoadingPlaylists: boolean): void {
    this.setState({
      ...this.state,
      isLoadingPlaylists: nextIsLoadingPlaylists,
    });
  }
  setArePlaylistsComplete(nextArePlaylistsComplete: boolean): void {
    this.setState({
      ...this.state,
      arePlaylistsComplete: nextArePlaylistsComplete,
    });
  }
  setHomePlaylists(nextHomePlaylists: Playlist[]): void {
    this.setState({ ...this.state, homePlaylists: nextHomePlaylists });
  }

  // GET PLAYLISTS FROM API

  async getPlaylists(
    {
      newSelectedSport,
      newFilters,
      newSorting,
      nextPage,
    }: {
      newSelectedSport?: Sport;
      newFilters?: BrowseFilter[];
      newSorting?: BrowseSorting;
      nextPage: boolean;
    } = { nextPage: false }
  ): Promise<void> {
    if (nextPage && this.state.arePlaylistsComplete) {
      return;
    }

    let goOn = !this.state.isLoadingPlaylists;
    while (!goOn) {
      await timeDelay(100);
      goOn = !this.state.isLoadingPlaylists;
    }

    if (!nextPage) {
      this.setPlaylists([]);
      this.setArePlaylistsComplete(false);
    }

    this.setIsLoadingPlaylists(true);

    try {
      const fetchedPlaylists: Playlist[] = await firstValueFrom(
        this.playlistApi.getPlaylists$(
          newSelectedSport,
          newFilters,
          newSorting,
          nextPage
        )
      );
      const newPlaylists: Playlist[] =
        this.getPlaylistsWithoutDuplicate(fetchedPlaylists);
      this.setPlaylists(newPlaylists);

      this.setArePlaylistsComplete(this.playlistApi.isLastPageLoaded);
    } catch (err) {
      this.setArePlaylistsComplete(true);
    } finally {
      this.setIsLoadingPlaylists(false);
    }
  }

  private getPlaylistsWithoutDuplicate(
    fetchedPlaylists: Playlist[]
  ): Playlist[] {
    const newPlaylistsWithDuplicates: Playlist[] = [
      ...this.state.playlists,
      ...fetchedPlaylists,
    ];
    const obj: {} = {};
    newPlaylistsWithDuplicates.forEach(
      (content) => (obj[content.id] = content)
    );
    return [
      ...new Set(newPlaylistsWithDuplicates.map((playlist) => playlist.id)),
    ].map((id) => obj[id]);
  }

  async getHomePlaylists(selectedSport: Sport): Promise<void> {
    this.setHomePlaylists(
      await firstValueFrom(this.playlistApi.getHomePlaylists$(selectedSport))
    );
  }
}
